import { useEffect, useState } from 'react'
import { auditApiService } from '../services/auditApiService'
import { ApiError } from '../services/BaseApiService'
import type { AuditEvent } from '../types'
import { colors, fontFamily, fontSize, radius, shadow, spacing } from '../styles/theme'

export default function AuditLogPage() {
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)

  async function load(p: number) {
    setLoading(true)
    try {
      const rows = await auditApiService.list(p)
      setEvents(rows)
      setError('')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load audit log')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load(page) }, [page])

  const thStyle: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`,
    textAlign: 'left',
    fontSize: fontSize.xs,
    fontWeight: 500,
    fontFamily: fontFamily.mono,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: colors.textSecondary,
    borderBottom: `1px solid ${colors.border}`,
    background: colors.background,
    whiteSpace: 'nowrap',
  }

  const tdStyle: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: fontSize.sm,
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
  }

  return (
    <div>
      <div style={{ marginBottom: spacing.xl, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: spacing.md }}>
        <div>
          <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, letterSpacing: '-0.02em', color: colors.text, margin: 0 }}>
            Audit Log
          </h1>
          <p style={{ color: colors.textSecondary, marginTop: spacing.xs, fontSize: fontSize.base }}>
            Immutable record of all admin and template actions.
          </p>
        </div>
        <a
          href={auditApiService.exportUrl(90)}
          download
          style={{
            padding: '7px 14px',
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.sm,
            fontSize: fontSize.sm,
            color: colors.text,
            textDecoration: 'none',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          Export CSV (90d)
        </a>
      </div>

      {error && <div style={{ color: colors.error, marginBottom: spacing.md }}>{error}</div>}

      <div style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: radius.md,
        boxShadow: shadow.sm,
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: spacing.xl, color: colors.textSecondary }}>Loading…</div>
        ) : events.length === 0 ? (
          <div style={{ padding: spacing.xl, color: colors.textSecondary }}>No audit events yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Time</th>
                <th style={thStyle}>Event</th>
                <th style={thStyle}>Entity</th>
                <th style={thStyle}>Actor</th>
              </tr>
            </thead>
            <tbody>
              {events.map(ev => (
                <tr key={ev.auditEventId}>
                  <td style={{ ...tdStyle, color: colors.textSecondary, whiteSpace: 'nowrap' }}>
                    {new Date(ev.createdAt).toLocaleString()}
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      fontFamily: fontFamily.mono,
                      fontSize: fontSize.xs,
                      background: colors.surfaceTinted,
                      padding: '2px 6px',
                      borderRadius: radius.sm,
                      border: `1px solid ${colors.border}`,
                    }}>
                      {ev.eventType}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {ev.entityType && (
                      <span style={{ color: colors.textSecondary }}>
                        {ev.entityType}
                        {ev.entityId && (
                          <span style={{ fontFamily: 'monospace', fontSize: '11px', marginLeft: 4 }}>
                            {ev.entityId.slice(0, 8)}…
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '11px', color: colors.textMuted }}>
                    {ev.actorUserId ? ev.actorUserId.slice(0, 8) + '…' : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.md, justifyContent: 'flex-end' }}>
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1 || loading}
          style={{
            padding: '6px 14px',
            background: 'none',
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            fontSize: fontSize.sm,
            cursor: 'pointer',
            color: colors.text,
            opacity: page === 1 ? 0.4 : 1,
          }}
        >
          Previous
        </button>
        <span style={{ padding: '6px 10px', fontSize: fontSize.sm, color: colors.textSecondary }}>
          Page {page}
        </span>
        <button
          onClick={() => setPage(p => p + 1)}
          disabled={events.length < 50 || loading}
          style={{
            padding: '6px 14px',
            background: 'none',
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            fontSize: fontSize.sm,
            cursor: 'pointer',
            color: colors.text,
            opacity: events.length < 50 ? 0.4 : 1,
          }}
        >
          Next
        </button>
      </div>
    </div>
  )
}
